import type { FxBlendMode, FxBlock, FxLayer } from "./fxTypes";

export type FxHybridLayerKind =
  | "distortion-layer"
  | "overlay-video"
  | "overlay-texture"
  | "particle-emitter"
  | "light-flash"
  | "motion-impulse"
  | "chromatic-aberration"
  | "motion-blur"
  | "vibration";

export type FxOverlayFormat = "webm-alpha" | "mov-prores4444" | "png-sequence";

export type FxHybridLayer = {
  id: string;
  kind: FxHybridLayerKind;
  label: string;
  enabledByDefault: boolean;
};

export type FxHybridPreset = {
  effectType: string;
  label: string;
  layers: readonly FxHybridLayer[];
  defaultParams: Readonly<Record<string, number>>;
  preferredOverlayFormats?: readonly FxOverlayFormat[];
};

export type FxOverlayAssetDescriptor = {
  id: string;
  label: string;
  family: string;
  format: FxOverlayFormat;
  shaderFamily: string;
};

export type FxCompositeOverlayPreset = {
  id: string;
  label: string;
  category: FxBlock["category"];
  effectType: string;
  durationSec: number;
  blendMode: FxBlendMode;
  opacity: number;
  layers: readonly FxLayer[];
};

const HYBRID_PRESETS: readonly FxHybridPreset[] = [
  {
    effectType: "screen-crack-impact",
    label: "Real Glass Impact",
    layers: [
      { id: "distort", kind: "distortion-layer", label: "Radial Distortion", enabledByDefault: true },
      { id: "crack", kind: "overlay-texture", label: "Crack Texture", enabledByDefault: true },
      { id: "shards", kind: "particle-emitter", label: "Particle Shards", enabledByDefault: true },
      { id: "flash", kind: "light-flash", label: "Light Flash", enabledByDefault: true },
      { id: "impulse", kind: "motion-impulse", label: "Motion Impulse", enabledByDefault: true },
      { id: "vibe", kind: "vibration", label: "Reactive Vibration", enabledByDefault: true },
    ],
    defaultParams: {
      intensity: 0.95,
      amplitude: 1.22,
      speed: 1.08,
      crackDensity: 0.84,
      radialDistortion: 0.46,
      shardDensity: 0.76,
      flashStrength: 0.82,
      vibrationStrength: 0.62,
      motionImpulse: 0.66,
      textureMix: 0.74,
      impactCenterX: 0.5,
      impactCenterY: 0.5,
    },
    preferredOverlayFormats: ["webm-alpha", "mov-prores4444", "png-sequence"],
  },
  {
    effectType: "shockwave",
    label: "Hybrid Shockwave",
    layers: [
      { id: "ring-distort", kind: "distortion-layer", label: "Ring Distortion", enabledByDefault: true },
      { id: "chroma", kind: "chromatic-aberration", label: "Chromatic Aberration", enabledByDefault: true },
      { id: "dust", kind: "particle-emitter", label: "Dust Particles", enabledByDefault: true },
      { id: "flash", kind: "light-flash", label: "Light Flash", enabledByDefault: true },
      { id: "mblur", kind: "motion-blur", label: "Motion Blur", enabledByDefault: true },
    ],
    defaultParams: {
      intensity: 0.9,
      amplitude: 1.16,
      speed: 1.04,
      ringDistortion: 0.74,
      chromaticStrength: 0.44,
      dustStrength: 0.62,
      flashStrength: 0.54,
      motionBlurStrength: 0.58,
      ringThickness: 0.24,
      impactCenterX: 0.5,
      impactCenterY: 0.5,
    },
    preferredOverlayFormats: ["webm-alpha", "png-sequence"],
  },
  {
    effectType: "impact-shockwave",
    label: "Impact Shockwave",
    layers: [
      { id: "ring-distort", kind: "distortion-layer", label: "Ring Distortion", enabledByDefault: true },
      { id: "chroma", kind: "chromatic-aberration", label: "Chromatic Aberration", enabledByDefault: true },
      { id: "dust", kind: "particle-emitter", label: "Dust Particles", enabledByDefault: true },
      { id: "flash", kind: "light-flash", label: "Light Flash", enabledByDefault: true },
      { id: "mblur", kind: "motion-blur", label: "Motion Blur", enabledByDefault: true },
      { id: "impulse", kind: "motion-impulse", label: "Motion Impulse", enabledByDefault: true },
    ],
    defaultParams: {
      intensity: 0.98,
      amplitude: 1.3,
      speed: 1.14,
      ringDistortion: 0.82,
      chromaticStrength: 0.5,
      dustStrength: 0.7,
      flashStrength: 0.64,
      motionBlurStrength: 0.66,
      ringThickness: 0.28,
      motionImpulse: 0.74,
      impactCenterX: 0.5,
      impactCenterY: 0.5,
    },
    preferredOverlayFormats: ["webm-alpha", "png-sequence"],
  },
];

const HYBRID_PRESET_BY_EFFECT = new Map<string, FxHybridPreset>(
  HYBRID_PRESETS.map((preset) => [preset.effectType.toLowerCase(), preset])
);

export const FX_OVERLAY_FORMAT_SUPPORT: Readonly<Record<FxOverlayFormat, Readonly<{ mimeHints: readonly string[]; notes: string }>>> = {
  "webm-alpha": {
    mimeHints: ["video/webm", "video/webm; codecs=vp8", "video/webm; codecs=vp9"],
    notes: "Alpha works when browser decoder supports transparent WebM stream.",
  },
  "mov-prores4444": {
    mimeHints: ["video/quicktime", "video/mov"],
    notes: "ProRes 4444 import is accepted; alpha playback depends on browser/OS codec support.",
  },
  "png-sequence": {
    mimeHints: ["image/png"],
    notes: "PNG sequence is represented as ordered PNG frame assets for timeline/compositor overlays.",
  },
};

const OVERLAY_FAMILIES: ReadonlyArray<{ slug: string; label: string; shaderFamily: string; format: FxOverlayFormat }> = [
  { slug: "glass-impact", label: "Glass Impact", shaderFamily: "screen-crack-impact", format: "webm-alpha" },
  { slug: "shock-ring", label: "Shock Ring", shaderFamily: "shockwave", format: "webm-alpha" },
  { slug: "impact-wave", label: "Impact Wave", shaderFamily: "impact-shockwave", format: "webm-alpha" },
  { slug: "lens-dust", label: "Lens Dust", shaderFamily: "lens-dust", format: "png-sequence" },
  { slug: "light-leak", label: "Light Leak", shaderFamily: "light-leak-sweep", format: "mov-prores4444" },
  { slug: "spark-burst", label: "Spark Burst", shaderFamily: "beat-particle-burst", format: "png-sequence" },
  { slug: "fog-layer", label: "Fog Layer", shaderFamily: "fog-depth-layer", format: "webm-alpha" },
  { slug: "fire-sparks", label: "Fire Sparks", shaderFamily: "fire-sparks", format: "png-sequence" },
  { slug: "water-drop", label: "Water Drop", shaderFamily: "water-droplets", format: "webm-alpha" },
  { slug: "heat-wave", label: "Heat Wave", shaderFamily: "heat-distortion", format: "webm-alpha" },
];

const FX_OVERLAY_LIBRARY_INTERNAL: FxOverlayAssetDescriptor[] = [];
for (const family of OVERLAY_FAMILIES) {
  for (let i = 1; i <= 5; i += 1) {
    FX_OVERLAY_LIBRARY_INTERNAL.push({
      id: `${family.slug}-${String(i).padStart(2, "0")}`,
      label: `${family.label} ${i}`,
      family: family.label,
      format: family.format,
      shaderFamily: family.shaderFamily,
    });
  }
}

export const FX_OVERLAY_LIBRARY: readonly FxOverlayAssetDescriptor[] = FX_OVERLAY_LIBRARY_INTERNAL;

const FX_COMPOSITE_OVERLAY_PRESETS: readonly FxCompositeOverlayPreset[] = [
  {
    id: "impact-shockwave",
    label: "impact-shockwave",
    category: "audio-reactive",
    effectType: "impact-shockwave",
    durationSec: 1,
    blendMode: "screen",
    opacity: 1,
    layers: [
      {
        id: "impact-shockwave-overlay-video",
        type: "overlay-video",
        src: "/fx-overlays/shockwave.webm",
        blendMode: "screen",
        opacity: 1,
        startOffset: 0,
        duration: 1,
        loop: false,
        playbackRate: 1,
        placementMode: "follow-target",
        positionX: 0,
        positionY: 0,
        positionZ: 0,
      },
    ],
  },
  {
    id: "cinematic-film-burn",
    label: "cinematic-film-burn",
    category: "filter",
    effectType: "film-burn",
    durationSec: 2.5,
    blendMode: "screen",
    opacity: 0.9,
    layers: [
      {
        id: "cinematic-film-burn-overlay-video",
        type: "overlay-video",
        src: "/fx-overlays/film-burn.webm",
        blendMode: "screen",
        opacity: 0.9,
        startOffset: 0,
        duration: 2.5,
        loop: false,
        playbackRate: 1,
        placementMode: "follow-target",
        positionX: 0,
        positionY: 0,
        positionZ: 0,
      },
    ],
  },
  {
    id: "atmosphere-embers",
    label: "atmosphere-embers",
    category: "overlay",
    effectType: "floating-embers",
    durationSec: 2,
    blendMode: "add",
    opacity: 0.75,
    layers: [
      {
        id: "atmosphere-embers-overlay-video",
        type: "overlay-video",
        src: "/fx-overlays/embers.webm",
        blendMode: "add",
        opacity: 0.75,
        startOffset: 0,
        duration: 2,
        loop: true,
        playbackRate: 1,
        placementMode: "follow-target",
        positionX: 0,
        positionY: 0,
        positionZ: 0,
      },
    ],
  },
];

const FX_COMPOSITE_OVERLAY_PRESET_BY_ID = new Map<string, FxCompositeOverlayPreset>(
  FX_COMPOSITE_OVERLAY_PRESETS.map((preset) => [preset.id.toLowerCase(), preset])
);

export function getFxHybridPreset(effectType: string | null | undefined): FxHybridPreset | null {
  const normalized = String(effectType || "").trim().toLowerCase();
  if (!normalized) return null;
  return HYBRID_PRESET_BY_EFFECT.get(normalized) ?? null;
}

export function getFxCompositeOverlayPresets(): readonly FxCompositeOverlayPreset[] {
  return FX_COMPOSITE_OVERLAY_PRESETS;
}

export function getFxCompositeOverlayPreset(
  presetId: string | null | undefined
): FxCompositeOverlayPreset | null {
  const normalized = String(presetId || "").trim().toLowerCase();
  if (!normalized) return null;
  return FX_COMPOSITE_OVERLAY_PRESET_BY_ID.get(normalized) ?? null;
}

export function resolveFxCompositeOverlayPresetForEffect(
  effectType: string | null | undefined
): FxCompositeOverlayPreset | null {
  return getFxCompositeOverlayPreset(effectType);
}

export function mergeFxHybridDefaults(
  effectType: string | null | undefined,
  params?: Record<string, number> | null
): Record<string, number> {
  const preset = getFxHybridPreset(effectType);
  if (!preset) {
    return {
      ...(params || {}),
    };
  }
  return {
    ...preset.defaultParams,
    ...(params || {}),
  };
}
